import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase.ts'; // Assuming firebase.js exports db
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './ScheduledTasksPage.css';
import { ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, Typography, SelectChangeEvent, Container, Box, Button } from '@mui/material'; // Import ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, Typography, and Button
import { useTheme } from './ThemeContext'; // Import useTheme
import MobileHeader from './components/MobileHeader'; // Import MobileHeader
import CustomCalendarToolbar from './components/CustomCalendarToolbar'; // Import CustomCalendarToolbar
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme

const localizer = momentLocalizer(moment);

// Helper function to map string day names to numbers (0-6, Sunday-Saturday)
const dayNameToNumber: { [key: string]: number } = {
  "Sunday": 0, "Monday": 1, "Tuesday": 2, "Wednesday": 3, "Thursday": 4, "Friday": 5, "Saturday": 6
};

// Helper function to map string nth values to numbers (1-4, -1 for last)
const nthToNumber: { [key: string]: number } = {
  "first": 1, "second": 2, "third": 3, "fourth": 4, "last": -1
};

// Helper function to find the Nth occurrence of a day of the week in a month
const findNthDayOfMonth = (year: number, month: number, dayOfWeek: number, nth: number): moment.Moment | null => {
  let date = moment([year, month, 1]); // Start at the 1st of the month
  let count = 0;

  if (nth === -1) { // Handle 'last'
    date = moment([year, month, date.daysInMonth()]); // Start at end of month
    while (date.day() !== dayOfWeek) {
      date.subtract(1, 'day');
    }
    return date;
  }

  while (count < nth) {
    if (date.day() === dayOfWeek) {
      count++;
    }
    if (count === nth) {
      return date;
    }
    date.add(1, 'day');
    if (date.month() !== month) { // Prevent infinite loop if nth day doesn't exist
      return null;
    }
  }
  return null;
};

// Helper function to generate future occurrences for display
export interface Task {
  id: string;
  name: string;
  description: string;
  checklistId: string;
  isRecurring: boolean;
  isRecurringInstance: boolean;
  recurrence: {
    type: string;
    details?: {
      days?: number[];
      day?: string;
      nth?: string;
    };
  };
  createdAt: any; // Firestore Timestamp
  nextOccurrenceDates: any[]; // Firestore Timestamps or Dates
  videoUrl?: string;
  completed?: boolean;
  orderIndex?: number;
}

interface Checklist {
  id: string;
  name: string;
}

interface ScheduledTasksPageProps {
  user: any; // User type from firebase/auth
}

const generateFutureOccurrences = (task: Task, numberOfMonths: number = 12): Date[] => {
  const occurrences: Date[] = [];
  const startDate = moment(task.createdAt.toDate()); // Use task.createdAt as the start date

  if (!task.isRecurring || !task.recurrence || task.recurrence.type === 'none') {
    return occurrences; // Not a recurring task
  }

  let currentDate = startDate;
  const endDate = moment().add(numberOfMonths, 'months').endOf('day');

  while (currentDate.isSameOrBefore(endDate)) {
    if (task.recurrence.type === 'weekly' && task.recurrence.details && task.recurrence.details.days) {
      // For weekly tasks, add occurrences only on specified days
      const dayOfWeek = currentDate.day(); // 0 for Sunday, 1 for Monday, etc.
      if (task.recurrence.details.days.includes(dayOfWeek)) {
        occurrences.push(currentDate.toDate());
      }
      currentDate = currentDate.add(1, 'days'); // Always advance daily for weekly to check all days
    } else if (task.recurrence.type === 'monthly' && task.recurrence.details && task.recurrence.details.day && task.recurrence.details.nth) {
      const targetDayOfWeek = dayNameToNumber[task.recurrence.details.day];
      const targetNth = nthToNumber[task.recurrence.details.nth];

      if (targetDayOfWeek !== undefined && targetNth !== undefined) {
        const occurrenceDate = findNthDayOfMonth(currentDate.year(), currentDate.month(), targetDayOfWeek, targetNth);
        if (occurrenceDate && occurrenceDate.isSameOrAfter(currentDate, 'day')) {
          occurrences.push(occurrenceDate.toDate());
        }
      }
      currentDate = currentDate.add(1, 'month').startOf('month'); // Move to the start of the next month
    } else {
      // For other recurrence types (daily, biweekly, yearly, or simple monthly)
      occurrences.push(currentDate.toDate());
      switch (task.recurrence.type) {
        case 'daily':
          currentDate = currentDate.add(1, 'days');
          break;
        case 'biweekly':
          currentDate = currentDate.add(2, 'weeks');
          break;
        case 'monthly':
          currentDate = currentDate.add(1, 'months');
          break;
        case 'yearly':
          currentDate = currentDate.add(1, 'years');
          break;
        default:
          // Handle unknown recurrence types or stop
          return occurrences;
      }
    }
  }
  return occurrences;
};

const ScheduledTasksPage = ({ user }: ScheduledTasksPageProps) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const { theme } = useTheme(); // Use the theme hook
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [selectedChecklist, setSelectedChecklist] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list'); // 'list' or 'calendar'
  const [selectedDateForPopup, setSelectedDateForPopup] = useState<Date | null>(null);
  const [tasksForSelectedDate, setTasksForSelectedDate] = useState<Task[]>([]);
  const [isCalendarViewEnabled, setIsCalendarViewEnabled] = useState<boolean>(false); // State for the switch

  const taskTypes = ['none', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']; // Use lowercase to match Firebase data

  useEffect(() => {
    const fetchTasksAndChecklists = async () => {
      if (!user) return;

      const fetchedChecklists: Checklist[] = [];
      const fetchedTasks: Task[] = [];

      // Fetch checklists first
      const checklistsRef = collection(db, 'users', user.uid, 'checklists');
      const checklistSnapshot = await getDocs(checklistsRef);
      checklistSnapshot.docs.forEach(doc => {
        fetchedChecklists.push({ id: doc.id, name: doc.data().name as string });
      });
      setChecklists(fetchedChecklists);

      // Then, for each checklist, fetch its tasks
      for (const checklist of fetchedChecklists) {
        const tasksRef = collection(db, 'users', user.uid, 'checklists', checklist.id, 'tasks');
        const q = query(tasksRef);
        const querySnapshot = await getDocs(q);
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          fetchedTasks.push({
            id: doc.id,
            name: data.name as string,
            description: data.description as string,
            checklistId: checklist.id, // Add checklistId to the task for filtering
            isRecurring: data.isRecurring || false,
            isRecurringInstance: data.isRecurringInstance || false,
            recurrence: data.recurrence && typeof data.recurrence === 'object' && data.recurrence.type
              ? data.recurrence
              : { type: 'none' }, // Ensure recurrence is an object with a type, default to 'none'
            createdAt: data.createdAt,
            nextOccurrenceDates: data.nextOccurrenceDates || [],
            videoUrl: data.videoUrl || '',
            completed: data.completed || false,
            orderIndex: data.orderIndex || 0,
          });
        });
      }

      // Client-side update for nextOccurrenceDates (fallback/immediate update)
      const tasksToUpdate: { id: string; checklistId: string; newDates: Date[] }[] = [];
      for (const task of fetchedTasks) {
        if (task.isRecurring && task.recurrence && task.recurrence.type !== 'none' && task.createdAt) {
          const now = moment().startOf('day');
          let needsUpdate = false;

          const validNextOccurrences = (task.nextOccurrenceDates || []).filter((ts: any) =>
            moment(ts.toDate()).isSameOrAfter(now)
          );

          if (validNextOccurrences.length < 3) { // Ensure at least 3 upcoming dates are present
            needsUpdate = true;
          }

          if (needsUpdate) {
            const newNextOccurrenceDates = generateFutureOccurrences(task, 1); // Generate for next 1 month for immediate update
            tasksToUpdate.push({
              id: task.id,
              checklistId: task.checklistId,
              newDates: newNextOccurrenceDates
            });
          }
        }
      }

      // Perform updates to Firestore
      for (const update of tasksToUpdate) {
        const taskDocRef = doc(db, 'users', user.uid, 'checklists', update.checklistId, 'tasks', update.id);
        await updateDoc(taskDocRef, {
          nextOccurrenceDates: update.newDates
        });
      }

      setTasks(fetchedTasks);
      setFilteredTasks(fetchedTasks); // Initialize filtered tasks
    };

    fetchTasksAndChecklists();
  }, [user]);

  useEffect(() => {
    let currentFilteredTasks = tasks;

    // First, filter based on recurrence logic (similar to ChecklistsPage)
    const today = moment().startOf('day'); // Normalize to start of day for comparison

    currentFilteredTasks = currentFilteredTasks.filter(task => {
      // Show non-recurring tasks and recurring templates
      if (!task.isRecurringInstance) {
        return true;
      }
      // Show recurring instances if their nextOccurrenceDates includes today or a past date
      if (task.isRecurringInstance && task.nextOccurrenceDates && task.nextOccurrenceDates.length > 0) {
        // Check if any of the nextOccurrenceDates are today or in the past
        return task.nextOccurrenceDates.some(dateTimestamp => {
          const date = moment(dateTimestamp.toDate()).startOf('day');
          return date.isSameOrBefore(today);
        });
      }
      return false; // Should not be reached for valid tasks
    });

    if (selectedChecklist) {
      currentFilteredTasks = currentFilteredTasks.filter(task => task.checklistId === selectedChecklist);
    }

    if (selectedType) {
      currentFilteredTasks = currentFilteredTasks.filter(task => task.recurrence.type === selectedType);
    }

    setFilteredTasks(currentFilteredTasks);
  }, [selectedChecklist, selectedType, tasks]);

  const handleChecklistFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedChecklist(e.target.value);
  };

  const handleTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedType(e.target.value);
  };

  const handleViewModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsCalendarViewEnabled(checked);
    setViewMode(checked ? 'calendar' : 'list');
  };

  const events = filteredTasks.flatMap((task: Task) => {
    let allOccurrences: Date[] = [];

    // Add existing nextOccurrenceDates (which are now kept up-to-date by the above logic)
    if (task.nextOccurrenceDates && task.nextOccurrenceDates.length > 0) {
      allOccurrences = allOccurrences.concat(task.nextOccurrenceDates.map((ts: any) => ts.toDate()));
    }

    // Generate additional future occurrences for calendar display (e.g., for the next 12 months)
    // This ensures the calendar shows more than just the 'next N' occurrences saved in Firestore
    if (task.isRecurring && task.recurrence && task.recurrence.type !== 'none' && task.createdAt) {
      const generatedForDisplay = generateFutureOccurrences(task, 12); // Use the original function for display
      const existingTimestamps = new Set(allOccurrences.map(d => d.getTime()));
      generatedForDisplay.forEach(date => {
        if (!existingTimestamps.has(date.getTime())) {
          allOccurrences.push(date);
        }
      });
    }

    return allOccurrences.map(date => ({
      title: task.name,
      start: date,
      end: date,
      allDay: true,
      resource: task,
    }));
  });

  return (
    <Container sx={{ pt: 0, px: 0 }} maxWidth={false}>
      {isMobile && <MobileHeader title="Scheduled Tasks" />}

      <Box sx={{ mt: isMobile ? '70px' : '30px', px: 2, pb: 2, backgroundColor: theme.colors.lightGrayBackground }}> {/* Adjusted mt */}
        <div className="filter-controls">
          <select onChange={handleChecklistFilterChange} value={selectedChecklist}>
          <option value="">All Checklists</option>
          {checklists.map(checklist => (
            <option key={checklist.id} value={checklist.id}>{checklist.name}</option>
          ))}
        </select>

        <select onChange={handleTypeFilterChange} value={selectedType}>
          <option value="">All Types</option>
          {taskTypes.map(type => (
            <option key={type} value={type}>{type === 'none' ? 'One-time' : type.charAt(0).toUpperCase() + type.slice(1)}</option>
          ))}
        </select>

        <FormControlLabel
          control={
            <Switch
              checked={isCalendarViewEnabled}
              onChange={handleViewModeChange}
              name="calendarViewToggle"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--theme-colors-primaryRed)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 0, 0, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--theme-colors-primaryRed)',
                },
              }}
            />
          }
          label={
            <Typography variant="body1" sx={{ color: 'var(--theme-colors-darkText)' }}>
              {isCalendarViewEnabled ? 'Calendar View' : 'List View'}
            </Typography>
          }
        />
        </div>

        {viewMode === 'list' ? (
          <div className="task-list">
            {filteredTasks.length > 0 ? (
            filteredTasks.map(task => (
              <div
                key={task.id}
                className="task-item"
                onClick={() => navigate(`/checklists/${task.checklistId}/tasks/${task.id}`)} // Add onClick handler
              >
                <h3>{task.name}</h3>
                <p>Checklist: {checklists.find(c => c.id === task.checklistId)?.name || 'N/A'}</p>
                <p>Type: {task.recurrence.type === 'none' ? 'One-time' : task.recurrence.type}</p>
              </div>
            ))
          ) : (
            <p>No scheduled tasks found.</p>
          )}
        </div>
      ) : (
        <div className="calendar-view">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 500 }}
            views={['month']} // Only show month view
            defaultView="month"
            onSelectEvent={(event: any) => {
              setSelectedDateForPopup(event.start);
              const tasksOnSelectedDay = events
                .filter(e => moment(e.start).isSame(event.start, 'day'))
                .map(e => e.resource as Task);
              setTasksForSelectedDate(tasksOnSelectedDay);
            }}
            onSelectSlot={(slotInfo: any) => {
              setSelectedDateForPopup(slotInfo.start);
              const tasksOnSelectedDay = events
                .filter(e => moment(e.start).isSame(slotInfo.start, 'day'))
                .map(e => e.resource as Task);
              setTasksForSelectedDate(tasksOnSelectedDay);
            }}
            selectable // Enable slot selection
            longPressThreshold={1} // Set a very low threshold to make simple clicks trigger onSelectSlot
            components={{
              toolbar: CustomCalendarToolbar,
              dateCellWrapper: ({ children, value }) => {
                const hasEvents = events.some(event => moment(event.start).isSame(value, 'day'));
                return (
                  <div className={hasEvents ? 'has-events' : ''}>
                    {children}
                  </div>
                );
              }
            }}
          />
          {selectedDateForPopup && tasksForSelectedDate.length > 0 && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: '8px', backgroundColor: theme.colors.whiteCardBackground }}>
              <Typography variant="h6" sx={{ color: theme.colors.darkText, mb: 2 }}>
                Tasks for {moment(selectedDateForPopup).format('MMMM Do YYYY')}
              </Typography>
              {tasksForSelectedDate.map(task => (
                <Box
                  key={task.id}
                  sx={{
                    mb: 1,
                    p: 1.5,
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    backgroundColor: theme.colors.lightGrayBackground,
                    cursor: 'pointer',
                    '&:hover': {
                    backgroundColor: theme.colors.lightGrayBackground,
                    },
                  }}
                  onClick={() => navigate(`/checklists/${task.checklistId}/tasks/${task.id}`)}
                >
                  <Typography variant="body1" sx={{ color: theme.colors.darkText, fontWeight: 'bold' }}>
                    {task.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.darkText }}>
                    {task.description}
                  </Typography>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedDateForPopup(null);
                  setTasksForSelectedDate([]);
                }}
                sx={{ mt: 2, color: theme.colors.primaryRed, borderColor: theme.colors.primaryRed }}
              >
                Clear Selection
              </Button>
            </Box>
          )}
        </div>
        )}
      </Box>
    </Container>
  );
};

export default ScheduledTasksPage;
